import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  UserAltIcon,
  RiderAltIcon,
  LocationAltIcon,
  InfoIcon,
  ClockIcon,
  PickupIcon
} from 'cokitchen-icons';
import { OrderType } from 'models/order.model';
import { formatNumber, getNameFromOrder, getOrderStatus, getUserName } from 'utils/functions';
import { calTotalKitchenTime, calculateDeliveryTime } from 'pages/order-details/util';
import useRoleContext from 'contexts/role.context';
import { ENUM_STATUS } from 'utils/enum';

type LiveTripCardBodyProps = {
  order: OrderType;
  setShowRiderProfile: (item: boolean) => void;
};

const CSLiveTripCardBody: FC<LiveTripCardBodyProps> = ({ order, setShowRiderProfile }) => {
  const [timeDifference, setTimeDifference] = useState({
    text: '',
    value: 0
  });
  const orderStatus = useMemo(() => getOrderStatus(order), [order]);
  const { role } = useRoleContext();

  const updateTimeDifference = ({ time, text }: { time: string; text: string }) => {
    const td = new Date(time).getTime() - new Date().getTime();
    const value = Math.floor((td / 1000 / 60) % 60);
    setTimeDifference({ text, value });
  };

  useEffect(() => {
    if (!role || order.pickup) return;

    const diffTime = calculateDeliveryTime({ order, orderStatus, role });
    updateTimeDifference(diffTime);

    const interval = setInterval(() => calculateDeliveryTime({ order, orderStatus, role }), 60000);
    return () => clearInterval(interval);
  }, [order, orderStatus, role]);

  const name = useMemo(() => {
    return getNameFromOrder(order);
  }, [order]);

  const rider_name = getUserName(order.rider, 'nil');
  const delivery_fee = order?.calculated_order?.delivery_fee;

  const timeDifferenceStyle =
    timeDifference.value > 15
      ? 'border border-[#ECEFEB] bg-[#EEF9EB] text-[#46AE31]'
      : timeDifference.value <= 15 && timeDifference.value > 5
      ? 'border border-[#EFEEEB] bg-[#F9F6EB] text-[#BD8F05]'
      : 'border border-[#EFEBEC] bg-[#FAEFF1] text-[#E60A2B]';
  return (
    <div className='space-y-3 px-3 py-3 grow'>
      <div className='flex justify-between'>
        <div className='text-sm text-gray-8 flex items-center gap-2'>
          <UserAltIcon className='text-gray-4 w-4 min-w-[16px]' />
          {name}
        </div>
        <div className='flex gap-2 items-center'>
          <div className='flex justify-between'>
            <div
              className='text-sm text-gray-8 flex items-center gap-2'
              onClick={(e) => {
                e.stopPropagation();
                setShowRiderProfile(true);
              }}>
              {order?.pickup ? (
                <>
                  <PickupIcon />
                  pickup
                </>
              ) : (
                <>
                  <RiderAltIcon className='text-gray-4 w-4 min-w-[16px]' />
                  {rider_name}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className='flex justify-between'>
        <div className='flex justify-between grow'>
          <div className='text-sm text-gray-8 flex items-center gap-2  w-[80%]'>
            <LocationAltIcon className='text-gray-4 min-w-[16px]' />
            <span>{order?.calculated_order?.address_details?.address_line}</span>
          </div>
        </div>
        <div className='text-xs min-w-fit text-[#313030'>{formatNumber(delivery_fee)}</div>
      </div>
      {[ENUM_STATUS.ONGOING, ENUM_STATUS.READY].includes(orderStatus) && (
        <div className='flex'>
          <div className='text-sm text-gray-8 flex items-center gap-2'>
            <ClockIcon className='text-gray-4 min-w-[16px]' />
            <span>Total kitchen time:</span>
          </div>
          <div className='text-[10px] rounded-[40px] ml-1 py-[3px] px-[10px] border border-[#EFEBEC] bg-[#EDEDED] text-[#858585]'>
            {' '}
            {calTotalKitchenTime({ order, orderStatus })} mins
          </div>
        </div>
      )}
      {timeDifference.text ? (
        <div className='flex gap-1 my-1 items-center'>
          <div className='flex'>
            <div className='text-sm text-gray-8 flex items-center gap-2'>
              <ClockIcon className='text-gray-4 min-w-[16px]' />
              <span>{timeDifference.text}: </span>
            </div>
          </div>
          <div className={`text-[10px] rounded-[40px] py-[3px] px-[10px] ${timeDifferenceStyle}`}>
            {timeDifference.value ? `${timeDifference.value} mins` : '--'}
          </div>
        </div>
      ) : null}
      {order?.order_details?.rider_note && (
        <div className='border border-[#FDE9AF] bg-[#FEF7E1] text-[#85806F] px-4 py-2 rounded-md text-xs'>
          <p className='flex gap-1 items-start'>
            <InfoIcon /> <span>{order?.order_details?.rider_note}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default CSLiveTripCardBody;
